import React from 'react';
import { useRecoilValue } from 'recoil';

import type * as api from '@gitbook/api-client';
import { filterOutNullable } from '@gitbook/util-ts';

import { getIndexesFromOrgAllContent, type OrgAllContentItem } from '../../../Components';
import { MAX_VISITED_ITEMS, recentlyVisitedContentSelector } from '../../../state';

/**
 * Get the current user's most recent content.
 *
 * If the user has viewed less than 6 content, we complete the
 * list with the most recently updated spaces in the org.
 */
export function useRecentlyEditedContent(props: {
    organizationId: string;
    orgContent: api.OrganizationAllContent;
}): OrgAllContentItem<api.OrganizationAllSite | api.OrganizationAllSpace>[] {
    const { organizationId, orgContent } = props;

    // Get most recently visited content from local storage
    const recentlyVisitedContentIds = useRecoilValue(
        recentlyVisitedContentSelector(organizationId)
    );

    return React.useMemo(() => {
        const { sites, spaces } = getIndexesFromOrgAllContent(orgContent);

        const last6UpdatedSpacesInOrg = Array.from(spaces.values())
            .sort((a, b) => {
                return (
                    new Date(b.item.space.updatedAt).getTime() -
                    new Date(a.item.space.updatedAt).getTime()
                );
            })
            .slice(0, MAX_VISITED_ITEMS);

        // If user has visited no content, we show the last 6 updated spaces in the org.
        if (recentlyVisitedContentIds.length === 0) {
            return last6UpdatedSpacesInOrg;
        }

        // Otherwise we show the user's recently visited content,
        // and complete the list with the last updated spaces in the org if needed.
        const recentlyVisitedContent = recentlyVisitedContentIds
            .map((contentId) => sites.get(contentId) || spaces.get(contentId))
            .filter(filterOutNullable);

        return recentlyVisitedContent.length >= MAX_VISITED_ITEMS
            ? recentlyVisitedContent
            : [
                  ...recentlyVisitedContent,
                  ...last6UpdatedSpacesInOrg
                      .filter(({ item }) => !recentlyVisitedContentIds.includes(item.space.id))
                      .slice(0, MAX_VISITED_ITEMS - recentlyVisitedContent.length),
              ];
    }, [orgContent, recentlyVisitedContentIds]);
}
